import { Box, MenuItem, type SelectChangeEvent, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { castArray, omit, pick, uniqBy } from 'lodash-es';
import { DateTime } from 'luxon';
import MarkdownIt from 'markdown-it';
import markdownItIns from 'markdown-it-ins';
import type { ChangeEvent, FC } from 'react';
import type { NumberFormatValues } from 'react-number-format';
import { DatePicker } from '../DatePicker';
import { FormControl } from '../FormControl';
import type { FormControlProps } from '../FormControl/FormControl.types';
import { MultiselectCheckboxes } from '../MultiselectCheckboxes';
import { NumberFormatWithWrapper } from '../NumberFormat';
import { PatternTextField } from '../PatternTextField';
import type { PatternTextFieldProps } from '../PatternTextField/PatternTextField.types';
import { PercentageInput } from '../PercentageInput';
import type { PercentageInputProps } from '../PercentageInput/PercentageInput.types';
import { Select } from '../Select';
import type { SelectProps } from '../Select/Select.types';
import { TextField } from '../TextField';
import type { TextFieldProps } from '../TextField/TextField.types';
import { ToggleButtonsGroup } from '../ToggleButtonsGroup';
import { maskedInputsConfig } from './QuestionEngine.constants';
import type { IRenderField, QuestionEngineOnChangeProps } from './QuestionEngine.types';

export const RenderField: FC<IRenderField> = ({
  field,
  formik,
  state,
  setState,
  isReadOnly,
  onChangeEmitted,
  // eslint-disable-next-line complexity
}) => {
  const testPrefix = `q_${field.code}`;
  const isFieldReadOnly = field?.is_readonly || isReadOnly;
  const isFieldOptional = field.is_optional;
  const fieldHasError = formik.touched[`${field.code}`] && Boolean(formik.errors[`${field.code}`]);
  const errorMessage = fieldHasError && (formik.errors[`${field.code}`] as string);

  const { emitOnChangedEvent, fs_mask } = field?.additional_data ?? {};

  const checkFieldShouldEmitChangedEvent = ({
    field: fieldName,
    value,
    oldValue,
  }: QuestionEngineOnChangeProps) => {
    if (emitOnChangedEvent) {
      onChangeEmitted?.({ field: fieldName, value, oldValue });
    }
  };

  const updateState = (name: string, value: unknown) => {
    const oldValue = state?.[`${name}`];

    setState?.((prevState) => ({ ...prevState, [name]: value }));

    formik?.setFieldValue(field.code, value);

    checkFieldShouldEmitChangedEvent({ field: name, value, oldValue });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>,
  ) => {
    const { name, value } = event.target;

    updateState(name, value);
  };

  /**
   * Get the raw value instead of formatted value
   *
   * For `PatternTextField` that is used with `valueIsNumericString`
   */
  const handlePatternFormatInputChange = (values: NumberFormatValues) => {
    const { value = '' } = values;

    updateState(field.code, value);
  };

  const commonTextFieldProps: TextFieldProps = {
    field,
    onChange: handleInputChange,
    testPrefix,
    isFieldReadOnly,
    isFieldOptional,
    value: state?.[field.code],
    error: fieldHasError,
    helperText: errorMessage,
    inputProps: {
      ...(fs_mask && { className: 'fs-mask' }),
    },
  };

  const commonFormControlProps: FormControlProps = {
    field,
    isFieldReadOnly,
    isFieldOptional,
    error: fieldHasError,
    errorMessage,
  };

  // Check if field exist in pre-defined masked input config
  if (maskedInputsConfig[field.code]) {
    const { format, mask } = maskedInputsConfig[field.code];

    return (
      <PatternTextField
        {...(omit(commonTextFieldProps, 'onChange') as PatternTextFieldProps)}
        onValueChange={handlePatternFormatInputChange}
        valueIsNumericString
        format={format}
        mask={mask}
      />
    );
  }

  switch (field.type) {
    case 'string': {
      return <TextField {...commonTextFieldProps} />;
    }

    case 'email': {
      return <TextField {...commonTextFieldProps} type="email" />;
    }

    case 'number': {
      return <NumberFormatWithWrapper {...commonTextFieldProps} helperText={errorMessage} />;
    }

    case 'currency': {
      return (
        <NumberFormatWithWrapper
          {...commonTextFieldProps}
          inputProps={{ ...commonTextFieldProps.inputProps, prefix: '$ ' }}
          helperText={errorMessage}
        />
      );
    }

    case 'year': {
      return (
        <PatternTextField
          {...(omit(commonTextFieldProps, 'onChange') as PatternTextFieldProps)}
          onValueChange={handlePatternFormatInputChange}
          valueIsNumericString
          format="####"
          inputProps={{ ...commonTextFieldProps.inputProps, inputMode: 'numeric' }}
        />
      );
    }

    case 'phone': {
      return (
        <PatternTextField
          {...(commonTextFieldProps as PatternTextFieldProps)}
          format="(###) ###-####"
          mask=" "
          type="tel"
          inputProps={{ ...commonTextFieldProps.inputProps, inputMode: 'tel' }}
        />
      );
    }

    case 'percentage': {
      return (
        <PercentageInput
          {...(commonTextFieldProps as PercentageInputProps)}
          initialValue={commonTextFieldProps.value as string}
          onInputChange={(val) => updateState(field.code, val)}
          inputProps={{ ...commonTextFieldProps.inputProps, inputMode: 'decimal' }}
        />
      );
    }

    case 'select': {
      const uniqueChoices = uniqBy([...(field.choices || [])], 'code');
      return (
        <FormControl {...commonFormControlProps} renderLabel>
          <Select
            id={field.code}
            name={field.code}
            labelId={`${field.code}_label`}
            label={field.name}
            {...(pick(commonTextFieldProps, [
              'value',
              'onChange',
              'testPrefix',
              'required',
              'error',
              'isFieldOptional',
            ]) as SelectProps)}
            optionsCount={uniqueChoices?.length}
            preSelectFirstOption={() => {
              const firstChoice = uniqueChoices.at(0);
              if (firstChoice) {
                updateState(field?.code, firstChoice?.code);
              }
            }}
          >
            {uniqueChoices?.map((type) => (
              <MenuItem key={type.code} value={type.code} data-test={type.name}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    case 'select_button': {
      return (
        <FormControl {...commonFormControlProps}>
          <ToggleButtonsGroup
            id={field.code}
            value={commonTextFieldProps.value}
            onChange={(_, value: string) => {
              updateState(field.code, value);
            }}
            options={
              field.choices?.map((choice) => ({
                ...choice,
                value: choice.code,
                label: choice.name,
              })) || []
            }
            testPrefix={testPrefix}
          />
        </FormControl>
      );
    }

    case 'multiselect': {
      return (
        <FormControl {...commonFormControlProps}>
          <MultiselectCheckboxes
            id={field.code}
            initialValue={commonTextFieldProps.value as string}
            options={field.choices || []}
            onChange={(value) => updateState(field.code, value)}
            testPrefix={testPrefix}
          />
        </FormControl>
      );
    }

    case 'date': {
      const currentDate = field?.additional_data?.disableFuture
        ? DateTime.now().startOf('day').toJSDate()
        : undefined;
      const minDate = DateTime.utc(1900, 1, 1).toJSDate();
      const isMonthDayYearView = Boolean(field?.additional_data?.monthDayYearView);

      return (
        <DatePicker
          value={(commonTextFieldProps.value as Date) || null}
          onChange={(value) => updateState(field.code, value)}
          label={field.name}
          maxDate={currentDate}
          minDate={minDate}
          error={commonTextFieldProps.error}
          slotProps={{
            textField: {
              ...pick(commonTextFieldProps, ['error', 'helperText']),
              inputProps: {
                'data-test': testPrefix,
              },
            },
          }}
          monthDayYearView={isMonthDayYearView}
        />
      );
    }

    default:
      return <></>;
  }
};

export const FieldQuestionText: FC<Pick<IRenderField, 'field'>> = ({ field }) => (
  <Typography variant="h2">{renderMarkdown(field?.question_text || '')}</Typography>
);

export const RenderFieldWithWrapper: FC<IRenderField> = ({ field, idx, sx, ...rest }) => (
  <Stack>
    {field?.question_text && (
      <Stack gap={1} sx={idx !== 0 ? { mt: 4.5 } : {}}>
        <FieldQuestionText field={field} />

        {field?.tooltip && <Typography>{renderMarkdown(field?.tooltip)}</Typography>}
      </Stack>
    )}

    <Stack sx={[...castArray(sx), { mt: 1.5 }]}>
      <RenderField field={field} {...rest} />
    </Stack>
  </Stack>
);

// Markdown syntax support function:
// _text_   -> <em>text</em>         -> italic
// **text** -> <strong>text</strong> -> bold
// ++text++ -> <u>text</u>           -> underlined

// Hoist MarkdownIt instance and configuration to module scope
const markdownParser = new MarkdownIt().use(markdownItIns);
// Remove paragraph tags so output stays inline (<span> instead of <p>)
markdownParser.renderer.rules.paragraph_open = () => '';
markdownParser.renderer.rules.paragraph_close = () => '';

export const renderMarkdown = (text: string) => {
  if (!text) return <></>;

  const rawResult = markdownParser.render(text.replace(/\\n/g, '\n'));

  const result = DOMPurify.sanitize(rawResult);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        whiteSpace: 'pre-line',
        '& br': { display: 'none' },
      }}
      dangerouslySetInnerHTML={{ __html: result }}
    />
  );
};
